/**
 * Budget tracker — where the month stands, category by category.
 *
 * Every category and cap comes from D1 (GET /transactions/status, which rolls up
 * budget_categories). Adding a category in Settings makes it appear here with no
 * code change. Uncategorized spend is surfaced hard, because until it's assigned
 * every number on this page is understating the truth.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { CategoryStatus } from '../../shared/types';
import { api, money } from '../../lib/api';
import { currentPeriod, periodLabel, todayISO } from './dates';
import { EMPTY_SUMMARY, fetchSummary, type TransactionSummary } from './summary';
import { fetchWeekSpend, mondayOf, type DaySpend } from './week';
import { WeekSpendChart } from './WeekSpendChart';
import { Card, EmptyState, ErrorState, LoadingCards, PageHeader, PeriodPicker, buttonClass, quietButtonClass } from './ui';
import MoneyExtras from './MoneyExtras';

const STATE_COPY: Record<CategoryStatus['state'], { label: string; glyph: string; chip: string; bar: string }> = {
  ok: {
    label: 'On track',
    glyph: '✓',
    chip: 'bg-sage-soft text-sage',
    bar: 'bg-ok',
  },
  near: {
    label: 'Close to cap',
    glyph: '!',
    chip: 'bg-honey-soft text-honey',
    bar: 'bg-near',
  },
  over: {
    label: 'Over cap',
    glyph: '▲',
    chip: 'bg-clay-soft text-over',
    bar: 'bg-over',
  },
};

function Bar({
  percent,
  className,
  label,
}: {
  percent: number;
  className: string;
  label: string;
}) {
  return (
    <div
      className="h-2 w-full overflow-hidden rounded-full bg-surface-sunk"
      role="progressbar"
      aria-valuenow={Math.round(percent)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      <div
        className={`h-full rounded-full transition-[width] ${className}`}
        style={{ width: `${Math.min(100, Math.max(percent, percent > 0 ? 2 : 0))}%` }}
      />
    </div>
  );
}

function CategoryCard({
  status,
  showWeek,
  period,
}: {
  status: CategoryStatus;
  showWeek: boolean;
  period: string;
}) {
  const copy = STATE_COPY[status.state];
  const overBy = status.remaining < 0 ? Math.abs(status.remaining) : 0;
  const weekPercent =
    status.weekly_target > 0 ? (status.week_spent / status.weekly_target) * 100 : 0;
  const weekOver = status.weekly_target > 0 && status.week_spent > status.weekly_target;

  // The whole card is a link into the transactions for this category, this month —
  // where the total actually came from. The backend already filters on category_id.
  return (
    <Link
      to={`/money/transactions?period=${period}&category_id=${status.id}`}
      className="group block rounded-card border border-border bg-surface p-5 transition-colors hover:border-sage/50 hover:bg-surface-sunk/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-sage/50 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="flex items-center gap-2 font-medium text-ink">
          <span
            aria-hidden="true"
            className="h-2.5 w-2.5 shrink-0 rounded-full"
            style={{ backgroundColor: status.color }}
          />
          {status.name}
        </h3>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${copy.chip}`}>
          <span aria-hidden="true">{copy.glyph} </span>
          {copy.label}
        </span>
      </div>

      <p className="mt-3 font-display text-2xl font-semibold tabular-nums text-ink">
        {money(status.spent)}
        <span className="ml-1 font-sans text-sm font-normal text-ink-soft">
          of {money(status.monthly_cap)}
        </span>
      </p>

      <div className="mt-3">
        <Bar
          percent={status.percent_used}
          className={copy.bar}
          label={`${status.name}: ${Math.round(status.percent_used)} percent of cap used`}
        />
        <p className="mt-2 flex flex-wrap justify-between gap-x-3 text-sm">
          <span className="text-ink-soft">{Math.round(status.percent_used)}% used</span>
          <span className={overBy > 0 ? 'font-medium text-over' : 'text-ink-soft'}>
            {overBy > 0 ? `${money(overBy)} over` : `${money(status.remaining)} left`}
          </span>
        </p>
      </div>

      {showWeek && (
        <div className="mt-4 border-t border-border pt-3">
          <div className="flex items-baseline justify-between gap-2 text-sm">
            <span className="text-ink-soft">This week</span>
            <span className="tabular-nums text-ink">
              {money(status.week_spent)}{' '}
              <span className="text-ink-soft">of {money(status.weekly_target)}</span>
            </span>
          </div>
          <div className="mt-2">
            <Bar
              percent={weekPercent}
              className={weekOver ? 'bg-near' : 'bg-dusk'}
              label={`${status.name}: week to date against the weekly target`}
            />
          </div>
          <p className={`mt-1.5 text-xs ${weekOver ? 'font-medium text-near' : 'text-ink-soft'}`}>
            {weekOver ? (
              <>
                <span aria-hidden="true">! </span>
                {money(status.week_spent - status.weekly_target)} past this week's pace
              </>
            ) : (
              `${money(Math.max(0, status.weekly_target - status.week_spent))} left this week`
            )}
          </p>
        </div>
      )}
      <p className="mt-4 flex items-center gap-1 text-sm font-medium text-sage">
        See transactions
        <span aria-hidden="true" className="transition-transform group-hover:translate-x-0.5">
          →
        </span>
      </p>
    </Link>
  );
}

export default function BudgetPage() {
  const [period, setPeriod] = useState(currentPeriod());
  const [status, setStatus] = useState<CategoryStatus[] | null>(null);
  const [uncategorized, setUncategorized] = useState<TransactionSummary>(EMPTY_SUMMARY);
  const [week, setWeek] = useState<DaySpend[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // The weekly chart is always the CURRENT Mon–Sun week, regardless of which
      // month is being browsed, and it only shows for the current month — so there's
      // no reason to fetch it for a closed-out period.
      const showWeek = period === currentPeriod();
      // The uncategorized banner is a claim about money missing from the numbers on
      // this page, so it has to be counted server-side over every uncategorized row
      // in the month — not summed from a capped page of them.
      const [rollup, uncat, days] = await Promise.all([
        api.get<CategoryStatus[]>(
          `/transactions/status?period=${period}&today=${todayISO()}`
        ),
        fetchSummary(`period=${period}&uncategorized=1`),
        showWeek ? fetchWeekSpend(mondayOf(todayISO()), 7) : Promise.resolve<DaySpend[]>([]),
      ]);
      setStatus(rollup);
      setUncategorized(uncat);
      setWeek(days);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    void load();
  }, [load]);

  const isCurrent = period === currentPeriod();
  const totalSpent = (status ?? []).reduce((sum, s) => sum + s.spent, 0);
  const totalCaps = (status ?? []).reduce((sum, s) => sum + s.monthly_cap, 0);
  const totalRemaining = totalCaps - totalSpent;
  const totalPercent = totalCaps > 0 ? (totalSpent / totalCaps) * 100 : 0;
  const overCount = (status ?? []).filter((s) => s.state === 'over').length;
  const uncategorizedCount = uncategorized.count;
  const uncategorizedTotal = uncategorized.total;

  // The app has no daily-budget field, so derive one the same way the weekly target
  // is derived: the month's caps spread evenly across its days. No caps, no line.
  const [pYear, pMonth] = period.split('-').map(Number);
  const daysInMonth = pYear && pMonth ? new Date(pYear, pMonth, 0).getDate() : 30;
  const dailyBudget = totalCaps > 0 ? Math.round(totalCaps / daysInMonth) : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Budget"
        subtitle={periodLabel(period)}
        action={
          <div className="flex flex-wrap items-center gap-2">
            <PeriodPicker period={period} onChange={setPeriod} />
            <Link to={`/money/transactions?period=${period}`} className={buttonClass}>
              Add a transaction
            </Link>
          </div>
        }
      />

      {error && !loading && <ErrorState message={error} onRetry={() => void load()} />}

      {loading && !status && <LoadingCards />}

      {status && status.length === 0 && !error && (
        <EmptyState
          title="No categories yet"
          body="The budget tracker is built out of your categories and their monthly caps. Add the first one in Settings — Groceries, Amazon, Dining, whatever you actually watch — and it will show up here."
          action={
            <Link to="/admin" className={buttonClass}>
              Set up categories
            </Link>
          }
        />
      )}

      {status && status.length > 0 && (
        <>
          {uncategorizedCount > 0 && (
            <Card className="border-honey/50 bg-honey-soft">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="font-medium text-ink">
                    <span aria-hidden="true">! </span>
                    {uncategorizedCount}{' '}
                    {uncategorizedCount === 1 ? 'transaction needs' : 'transactions need'} a
                    category
                  </p>
                  <p className="mt-1 text-sm text-ink-soft">
                    <span className="font-medium tabular-nums">{money(uncategorizedTotal)}</span> is
                    missing from the numbers below until these are assigned.
                  </p>
                </div>
                <Link
                  to={`/money/transactions?period=${period}&uncategorized=1`}
                  className={buttonClass}
                >
                  Categorize them
                </Link>
              </div>
            </Card>
          )}

          <Card>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="text-sm text-ink-soft">Spent so far</p>
                <p className="mt-1 font-display text-3xl font-semibold tabular-nums text-ink">
                  {money(totalSpent)}
                  <span className="ml-1 font-sans text-sm font-normal text-ink-soft">
                    of {money(totalCaps)} in caps
                  </span>
                </p>
              </div>
              <p className="text-sm">
                {totalRemaining >= 0 ? (
                  <span className="text-ink-soft">
                    <span className="font-medium tabular-nums text-ink">
                      {money(totalRemaining)}
                    </span>{' '}
                    left across every category
                  </span>
                ) : (
                  <span className="font-medium text-over">
                    <span aria-hidden="true">▲ </span>
                    {money(Math.abs(totalRemaining))} over the household caps
                  </span>
                )}
              </p>
            </div>
            <div className="mt-3">
              <Bar
                percent={totalPercent}
                className={totalRemaining < 0 ? 'bg-over' : totalPercent >= 80 ? 'bg-near' : 'bg-ok'}
                label={`Household: ${Math.round(totalPercent)} percent of all caps used`}
              />
            </div>
            {overCount > 0 && (
              <p className="mt-2 text-sm font-medium text-over">
                <span aria-hidden="true">▲ </span>
                {overCount} {overCount === 1 ? 'category is' : 'categories are'} over cap this month
              </p>
            )}
          </Card>

          {isCurrent && (
            <Card>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="font-display text-lg font-semibold text-ink">Spending this week</h2>
                <p className="text-sm text-ink-soft">Mon–Sun, this week to date</p>
              </div>
              <div className="mt-4">
                <WeekSpendChart days={week} dailyBudget={dailyBudget} today={todayISO()} />
              </div>
              <p className="mt-3 text-xs text-ink-faint">
                {dailyBudget != null ? (
                  <>
                    The daily budget is the month's caps spread across its {daysInMonth} days
                    &mdash; {money(dailyBudget)} a day. Days above the line ran over that pace.
                  </>
                ) : (
                  'Set category caps in Settings to draw your daily budget line here.'
                )}{' '}
                Bars count every transaction that day, categorized or not.
              </p>
            </Card>
          )}

          {totalSpent === 0 && uncategorizedCount === 0 && (
            <EmptyState
              title={`Nothing logged for ${periodLabel(period)} yet`}
              body="Every category starts the month at zero. Key in the first receipt and the tracker fills in from there."
              action={
                <Link to={`/money/transactions?period=${period}`} className={buttonClass}>
                  Add the first transaction
                </Link>
              }
            />
          )}

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {status.map((s) => (
              <CategoryCard key={s.id} status={s} showWeek={isCurrent} period={period} />
            ))}
          </div>

          {/* Insights and Insurance, under the category cards. Insights was only reachable
              from a link at the bottom of this page and Insurance had no way in at all. */}
          <MoneyExtras />

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-ink-faint">
              {isCurrent
                ? "Weekly target is the monthly cap spread across the month's days, taken seven at a time."
                : `Weekly pacing only shows for the current month. ${periodLabel(period)} is closed out.`}{' '}
              Totals reflect what's been entered here — nothing else.
            </p>
            <Link to="/admin" className={quietButtonClass}>
              Edit categories &amp; caps
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
